import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import qualityMetricService from '../../services/qualityMetricService';
import projectService from '../../services/projectService';
import userService from '../../services/userService';

const QualityMetricEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    project_id: '',
    metric_name: '',
    metric_type: 'Code Quality',
    measurement_unit: '',
    target_value: '',
    actual_value: '',
    threshold_min: '',
    threshold_max: '',
    measurement_date: '',
    measurement_period: 'Monthly',
    trend: 'N/A',
    category: 'Product',
    priority: 'Medium',
    data_source: '',
    calculation_method: '',
    description: '',
    notes: '',
    action_items: '',
    responsible_user_id: '',
    is_active: true
  });

  const metricTypes = [
    'Defect Density', 'Code Quality', 'Test Coverage', 'Customer Satisfaction',
    'Performance', 'Security', 'Maintainability', 'Reliability',
    'Usability', 'Compliance', 'Documentation Quality', 'Code Review',
    'Technical Debt', 'Other'
  ];

  const trends = ['Improving', 'Stable', 'Declining', 'N/A'];
  const categories = ['Process', 'Product', 'Project', 'Resource', 'Customer'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  const measurementPeriods = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

  useEffect(() => {
    fetchQualityMetric();
    fetchProjects();
    fetchUsers();
  }, [id]);

  const fetchQualityMetric = async () => {
    try {
      setLoading(true);
      const response = await qualityMetricService.getQualityMetric(id);
       const data = response.data;
      setFormData({
        project_id: data.project_id || '',
        metric_name: data.metric_name || '',
        metric_type: data.metric_type || 'Code Quality',
        measurement_unit: data.measurement_unit || '',
        target_value: data.target_value || '',
        actual_value: data.actual_value || '',
        threshold_min: data.threshold_min || '',
        threshold_max: data.threshold_max || '',
        measurement_date: data.measurement_date ? data.measurement_date.split('T')[0] : '',
        measurement_period: data.measurement_period || 'Monthly',
        trend: data.trend || 'N/A',
        category: data.category || 'Product',
        priority: data.priority || 'Medium',
        data_source: data.data_source || '',
        calculation_method: data.calculation_method || '',
        description: data.description || '',
        notes: data.notes || '',
        action_items: data.action_items || '',
        responsible_user_id: data.responsible_user_id || '',
        is_active: data.is_active !== undefined ? data.is_active : true
      });
    } catch (error) {
      toast.error('Failed to fetch quality metric');
      console.error('Error fetching quality metric:', error);
      navigate('/app/quality-metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll({ limit: 1000 });
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.metric_name.trim()) {
      toast.error('Please enter a metric name');
      return;
    }
    if (!formData.target_value || formData.target_value <= 0) {
      toast.error('Please enter a valid target value');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare data
      const submitData = {
        ...formData,
        target_value: parseFloat(formData.target_value),
        actual_value: formData.actual_value ? parseFloat(formData.actual_value) : null,
        threshold_min: formData.threshold_min ? parseFloat(formData.threshold_min) : null,
        threshold_max: formData.threshold_max ? parseFloat(formData.threshold_max) : null,
        responsible_user_id: formData.responsible_user_id || null
      };

      await qualityMetricService.updateQualityMetric(id, submitData);
      toast.success('Quality metric updated successfully');
      navigate('/app/quality-metrics');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update quality metric');
      console.error('Error updating quality metric:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading quality metric...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Quality Metric</h1>
        <p className="text-gray-600 mt-1">Update quality metric information</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metric Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="metric_name"
                value={formData.metric_name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Code Coverage Rate"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metric Type <span className="text-red-500">*</span>
              </label>
              <select
                name="metric_type"
                value={formData.metric_type}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {metricTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {priorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Period <span className="text-red-500">*</span>
              </label>
              <select
                name="measurement_period"
                value={formData.measurement_period}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {measurementPeriods.map(period => (
                  <option key={period} value={period}>{period}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Measurement Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Measurement Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Unit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="measurement_unit"
                value={formData.measurement_unit}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., %, count, score"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="measurement_date"
                value={formData.measurement_date}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Value <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="target_value"
                value={formData.target_value}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 80"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actual Value
              </label>
              <input
                type="number"
                step="0.01"
                name="actual_value"
                value={formData.actual_value}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 75"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Threshold Min
              </label>
              <input
                type="number"
                step="0.01"
                name="threshold_min"
                value={formData.threshold_min}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 70"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Threshold Max
              </label>
              <input
                type="number"
                step="0.01"
                name="threshold_max"
                value={formData.threshold_max}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 90"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trend
              </label>
              <select
                name="trend"
                value={formData.trend}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {trends.map(trend => (
                  <option key={trend} value={trend}>{trend}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Responsible User
              </label>
              <select
                name="responsible_user_id"
                value={formData.responsible_user_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a user</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Source
              </label>
              <input
                type="text"
                name="data_source"
                value={formData.data_source}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., SonarQube, Manual Review"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Calculation Method
              </label>
              <input
                type="text"
                name="calculation_method"
                value={formData.calculation_method}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., (Lines Covered / Total Lines) * 100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the quality metric..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Additional notes..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Items
              </label>
              <textarea
                name="action_items"
                value={formData.action_items}
                onChange={handleChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="List action items to improve this metric..."
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/app/quality-metrics')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <FiX /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QualityMetricEdit;

